import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Smile, Frown, CloudRain, Minus, FileText } from "lucide-react";
import api from "../lib/api";
import { IconRenderer } from "../lib/icons";

const MOODS = {
  happy: { label: "Happy", Icon: Smile, bg: "bg-[#48BB78]/15 border-[#48BB78]", text: "text-[#22543D]" },
  okay: { label: "Okay", Icon: CloudRain, bg: "bg-[#ECC94B]/15 border-[#ECC94B]", text: "text-[#975A16]" },
  sad: { label: "Sad", Icon: Frown, bg: "bg-[#4299E1]/15 border-[#4299E1]", text: "text-[#2B6CB0]" },
};

export default function StatusPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/status/${token}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-background grid place-items-center p-6">
        <p data-testid="status-error" className="bg-white rounded-3xl border-2 border-foreground/10 p-8 text-lg text-muted-foreground max-w-md text-center">
          This status link has expired or is invalid. Ask the carer for a fresh one.
        </p>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-background grid place-items-center"><p className="text-xl font-bold text-muted-foreground">Loading…</p></div>;
  }

  const mood = data.mood ? MOODS[data.mood] : null;
  const allDone = data.routines.length > 0 && data.routines.every((r) => r.total > 0 && r.done >= r.total);

  return (
    <div data-testid="status-page" className="min-h-screen bg-background">
      <main className="max-w-md mx-auto px-4 py-10">
        <p className="text-center text-sm font-bold uppercase tracking-widest text-muted-foreground">RoutineBuddy status</p>
        <h1 data-testid="status-name" className="mt-2 text-4xl font-extrabold text-center">{data.name}</h1>
        <p className="mt-1 text-center text-lg font-semibold text-muted-foreground">
          {new Date(data.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
        </p>

        <div className={`mt-8 rounded-3xl border-2 p-6 flex items-center gap-4 ${mood ? mood.bg : "bg-white border-foreground/15"}`} data-testid="status-mood">
          {mood ? <mood.Icon size={48} strokeWidth={2.5} className={mood.text} /> : <Minus size={48} strokeWidth={2.5} className="text-muted-foreground" />}
          <div>
            <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Mood today</p>
            <p className={`text-3xl font-extrabold ${mood ? mood.text : ""}`}>{mood ? mood.label : "Not logged yet"}</p>
          </div>
        </div>

        <div className={`mt-4 rounded-3xl border-2 p-6 ${allDone ? "bg-[#48BB78]/15 border-[#48BB78]" : "bg-white border-foreground/15"}`} data-testid="status-routines">
          <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Routines today</p>
          {data.routines.length === 0 ? (
            <p className="mt-2 text-xl font-bold text-muted-foreground">No routines set up</p>
          ) : (
            <div className="mt-3 space-y-3">
              {data.routines.map((r, i) => {
                const complete = r.total > 0 && r.done >= r.total;
                return (
                  <div key={i} data-testid={`status-routine-${i}`} className="flex items-center gap-3">
                    <span className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${complete ? "bg-[#48BB78]" : "bg-secondary border-2 border-foreground/15"}`}>
                      {complete ? <Check size={22} strokeWidth={3.5} className="text-white" /> : <IconRenderer name={r.icon} size={20} />}
                    </span>
                    <span className="flex-1 text-lg font-extrabold truncate">{r.name}</span>
                    <span className={`text-lg font-extrabold ${complete ? "text-[#2F855A]" : "text-muted-foreground"}`}>{r.done}/{r.total}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4 rounded-3xl border-2 border-foreground/15 bg-white p-6 flex items-center gap-4" data-testid="status-note">
          <FileText size={40} strokeWidth={2.5} className="text-[#9F7AEA] shrink-0" />
          <div>
            <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Latest care note</p>
            <p className="text-xl font-extrabold">
              {data.last_note_at
                ? `${new Date(data.last_note_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} by ${data.last_note_by}`
                : "None yet today"}
            </p>
          </div>
        </div>

        <p className="mt-10 text-center text-sm font-semibold text-muted-foreground">Live view — refresh anytime. Made with RoutineBuddy.</p>
      </main>
    </div>
  );
}
